<template>
  <AppLayout 
    page-title="Job Marketplace" 
    page-subtitle="Find and apply for oilfield jobs"
  >
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        Job Marketplace
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Find Your Next Job</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">Browse available oilfield positions, apply directly, and track your applications</p>
    </div>

    <!-- Search and Filters -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-2">Search Jobs</label>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Job title, company, or keywords..."
            class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
          >
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-2">Location</label>
          <select 
            v-model="locationFilter"
            class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
          >
            <option value="">All Locations</option>
            <option value="texas">Texas</option>
            <option value="louisiana">Louisiana</option>
            <option value="oklahoma">Oklahoma</option>
            <option value="new-mexico">New Mexico</option>
            <option value="colorado">Colorado</option>
            <option value="wyoming">Wyoming</option>
            <option value="north-dakota">North Dakota</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-2">Job Type</label>
          <select 
            v-model="jobTypeFilter"
            class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
          >
            <option value="">All Types</option>
            <option value="full-time">Full Time</option>
            <option value="part-time">Part Time</option>
            <option value="contract">Contract</option>
            <option value="temporary">Temporary</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-2">Experience Level</label>
          <select 
            v-model="experienceFilter"
            class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
          >
            <option value="">All Levels</option>
            <option value="entry">Entry Level</option>
            <option value="mid">Mid Level</option>
            <option value="senior">Senior Level</option>
            <option value="expert">Expert Level</option>
          </select>
        </div>
      </div>
      <div class="flex justify-between items-center mt-6">
        <div class="text-sm text-slate-400">
          {{ totalJobs }} jobs found
        </div>
        <button 
          @click="clearFilters"
          class="px-4 py-2 text-emerald-400 hover:text-emerald-300 transition-colors"
        >
          Clear Filters
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
        <p class="text-slate-400">Loading jobs...</p>
      </div>
    </div>

    <!-- Job Listings -->
    <div v-else class="space-y-6">
      <div v-for="job in filteredJobs" :key="job.id" class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center space-x-3 mb-3">
              <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd"/>
                  <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"/>
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-white">{{ job.title }}</h3>
                <p class="text-emerald-400 font-medium">{{ job.company }}</p>
              </div>
            </div>
            
            <p class="text-slate-300 mb-4">{{ job.description }}</p>
            
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <span class="text-sm text-slate-300">{{ job.location }}</span>
              </div>
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span class="text-sm text-slate-300">${{ job.salary }}</span>
              </div>
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6z"/>
                </svg>
                <span class="text-sm text-slate-300">{{ job.type }}</span>
              </div>
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span class="text-sm text-slate-300">{{ job.experience }}</span>
              </div>
            </div>
            
            <div class="flex flex-wrap gap-2 mb-4">
              <span 
                v-for="skill in job.skills" 
                :key="skill"
                class="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full border border-emerald-500/30"
              >
                {{ skill }}
              </span>
            </div>
          </div>
          
          <div class="flex flex-col space-y-3 ml-6">
            <button 
              @click="applyToJob(job.id)"
              class="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors"
            >
              Apply Now
            </button>
            <button 
              @click="saveJob(job.id)"
              class="px-6 py-3 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors"
            >
              Save Job
            </button>
          </div>
        </div>
        
        <div class="flex items-center justify-between pt-4 border-t border-slate-700/50">
          <div class="flex items-center space-x-4 text-sm text-slate-400">
            <span>Posted {{ formatDate(job.postedDate) }}</span>
            <span>{{ job.applications }} applications</span>
          </div>
          <div class="flex items-center space-x-2">
            <span class="text-sm text-slate-400">Urgency:</span>
            <span :class="getUrgencyClass(job.urgency)" class="px-2 py-1 text-xs rounded-full">
              {{ job.urgency }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More Button -->
    <div class="text-center mt-8 px-8">
      <button 
        @click="loadMoreJobs"
        class="px-8 py-4 bg-slate-700/50 hover:bg-slate-600/50 text-white font-medium rounded-lg transition-colors"
      >
        Load More Jobs
      </button>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import AppLayout from '../components/layout/AppLayout.vue'
import { useUserStore } from '../stores/userStore'

const userStore = useUserStore()

// Reactive data
const isLoading = ref(true)
const searchQuery = ref('')
const locationFilter = ref('')
const jobTypeFilter = ref('')
const experienceFilter = ref('')
const currentPage = ref(1)
const totalPages = ref(1)
const totalJobs = ref(0)

const jobs = ref([])

// Computed properties
const filteredJobs = computed(() => {
  return jobs.value.filter(job => {
    const matchesSearch = !searchQuery.value || 
      job.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      job.company.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      job.description.toLowerCase().includes(searchQuery.value.toLowerCase())
    
    const matchesLocation = !locationFilter.value || 
      job.location.toLowerCase().includes(locationFilter.value.toLowerCase())
    
    const matchesType = !jobTypeFilter.value || 
      job.type.toLowerCase().includes(jobTypeFilter.value.toLowerCase())
    
    const matchesExperience = !experienceFilter.value || 
      job.experience.toLowerCase().includes(experienceFilter.value.toLowerCase())
    
    return matchesSearch && matchesLocation && matchesType && matchesExperience
  })
})

// API Methods
const fetchJobs = async () => {
  try {
    isLoading.value = true
    
    const params = new URLSearchParams({
      page: currentPage.value.toString(),
      limit: '20'
    })
    
    if (searchQuery.value) params.append('search', searchQuery.value)
    if (locationFilter.value) params.append('location', locationFilter.value)
    if (jobTypeFilter.value) params.append('jobType', jobTypeFilter.value)
    if (experienceFilter.value) params.append('experience', experienceFilter.value)
    
    const response = await fetch(`/api/jobs/list?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      jobs.value = data.jobs
      totalJobs.value = data.pagination.total
      totalPages.value = data.pagination.totalPages
    } else {
      console.error('Error fetching jobs')
    }
  } catch (error) {
    console.error('Error fetching jobs:', error)
  } finally {
    isLoading.value = false
  }
}

const applyToJob = async (jobId: number) => {
  try {
    const response = await fetch('/api/jobs/apply', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: JSON.stringify({
        jobId: jobId,
        coverLetter: '',
        resumeUrl: '',
        expectedSalary: null,
        availabilityDate: null
      })
    })
    
    if (response.ok) {
      const result = await response.json()
      alert(`Application submitted successfully to ${result.companyName}!`)
      await fetchJobs() // Refresh jobs to update application status
    } else {
      const error = await response.json()
      alert(error.error || 'Error applying to job')
    }
  } catch (error) {
    console.error('Error applying to job:', error)
    alert('Error applying to job')
  }
}

const saveJob = async (jobId: number) => {
  try {
    const job = jobs.value.find(j => j.id === jobId)
    if (job?.isSaved) {
      // Unsave job
      const response = await fetch(`/api/jobs/save?jobId=${jobId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userStore.authToken}`
        }
      })
      
      if (response.ok) {
        job.isSaved = false
      }
    } else {
      // Save job
      const response = await fetch('/api/jobs/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userStore.authToken}`
        },
        body: JSON.stringify({ jobId: jobId })
      })
      
      if (response.ok) {
        const job = jobs.value.find(j => j.id === jobId)
        if (job) job.isSaved = true
      }
    }
  } catch (error) {
    console.error('Error saving job:', error)
  }
}

const clearFilters = () => {
  searchQuery.value = ''
  locationFilter.value = ''
  jobTypeFilter.value = ''
  experienceFilter.value = ''
  currentPage.value = 1
  fetchJobs()
}

const loadMoreJobs = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    fetchJobs()
  }
}

// Watch for filter changes
watch([searchQuery, locationFilter, jobTypeFilter, experienceFilter], () => {
  currentPage.value = 1
  fetchJobs()
}, { debounce: 300 })

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
}

const getUrgencyClass = (urgency: string) => {
  const classes: { [key: string]: string } = {
    'High': 'bg-red-500/20 text-red-400 border border-red-500/30',
    'Medium': 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
    'Low': 'bg-green-500/20 text-green-400 border border-green-500/30'
  }
  return classes[urgency] || 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
}

// Load data on mount
onMounted(() => {
  fetchJobs()
})
</script>
